import React from 'react';
import { FormattedMessage, defineMessages, type MessageDescriptor } from 'react-intl';

import MarkdownIcon from '@atlaskit/icon/core/markdown';

import wordIcon from '../../img/word.svg';
import googleDocsIcon from '../../img/googleDocs.svg';
import OneDriveIcon from '../../img/oneDriveLogo.svg';
import type { SourcesItemPropsData } from '../SourcesListCard/SourcesItem.tsx';
import type { ImportStrategyComponentType } from '../types.ts';

// oxlint-disable-next-line import/no-cycle
import { ImportFromLocal } from './ImportFromLocal/ImportFromLocal.tsx';
// oxlint-disable-next-line import/no-cycle
import { ImportFromGD } from './ImportFromGD/ImportFromGD.tsx';
// oxlint-disable-next-line import/no-cycle
import { ImportFromOneDrive } from './ImportFromOneDrive/ImportFromOneDrive.tsx';
import { ImportFromMarkdown } from './ImportFromMarkdown/ImportFromMarkdown.tsx';
import { ActiveImports, IMPORT_FOLDER_NAMES } from './importStrategyDefinitions.ts';

export type ImportStrategy = {
	key: string;
	folderName: MessageDescriptor;
	component: ImportStrategyComponentType;
	card: SourcesItemPropsData;
};

const i18n = defineMessages({
	oneDriveSourcesListCardDataName: {
		id: 'import-from-sources.sources-list.oneDriveSourcesListCardDataName',
		defaultMessage: 'OneDrive',
		description: 'Icon title for uploading document from Microsoft OneDrive',
	},
	msWordSourcesCardDataName: {
		id: 'import-from-sources.sources-list.msWordCardDataName',
		defaultMessage: 'Microsoft Word',
		description: 'Icon title for uploading Microsoft Word document',
	},
	gdocSourcesCardDataName: {
		id: 'import-from-sources.sources-list.gdocCardDataName',
		defaultMessage: 'Google Docs',
		description: 'Icon title for uploading document from Google Drive',
	},
	markdownSourcesCardDataName: {
		id: 'import-from-sources.sources-list.markdownCardDataName',
		defaultMessage: 'Markdown',
		description: 'Icon title for importing a Markdown file',
	},
	markdownSourcesCardStatus: {
		id: 'import-from-sources.sources-list.markdownCardStatus',
		defaultMessage: 'TRY',
		description: 'Promotional status shown next to the Markdown import option',
	},
});

export const MARKDOWN_IMPORT_STRATEGY: ImportStrategy = {
	key: ActiveImports.IMPORT_FROM_MARKDOWN,
	folderName: IMPORT_FOLDER_NAMES[ActiveImports.IMPORT_FROM_MARKDOWN],
	component: ImportFromMarkdown,
	card: {
		name: <FormattedMessage {...i18n.markdownSourcesCardDataName} />,
		iconURL: '',
		icon: <MarkdownIcon label="" size="medium" />,
		status: <FormattedMessage {...i18n.markdownSourcesCardStatus} />,
	},
};

export const IMPORT_STRATEGIES: ImportStrategy[] = [
	{
		key: ActiveImports.IMPORT_FROM_LOCAL,
		folderName: IMPORT_FOLDER_NAMES[ActiveImports.IMPORT_FROM_LOCAL],
		component: ImportFromLocal,
		card: {
			name: <FormattedMessage {...i18n.msWordSourcesCardDataName} />,
			iconURL: wordIcon,
		},
	},
	{
		key: ActiveImports.IMPORT_FROM_GD,
		folderName: IMPORT_FOLDER_NAMES[ActiveImports.IMPORT_FROM_GD],
		component: ImportFromGD,
		card: {
			name: <FormattedMessage {...i18n.gdocSourcesCardDataName} />,
			iconURL: googleDocsIcon,
		},
	},
	{
		key: ActiveImports.IMPORT_FROM_ONE_DRIVE,
		folderName: IMPORT_FOLDER_NAMES[ActiveImports.IMPORT_FROM_ONE_DRIVE],
		component: ImportFromOneDrive,
		card: {
			name: <FormattedMessage {...i18n.oneDriveSourcesListCardDataName} />,
			iconURL: OneDriveIcon,
		},
	},
];
